/**
 * Dashboard Layout Component
 * Main layout wrapper for the AccountsGenie dashboard with sidebar navigation
 * Following PRD Phase 2 specifications for responsive design
 */

import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useConnectionStore } from '../../stores/connectionStore';
import { useDashboardStore } from '../../stores/dashboardStore';
import { trackComponentMount } from '../../utils/debugUtils';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import { cn } from '../../lib/utils';

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  // Track component mount for debugging
  React.useEffect(() => {
    trackComponentMount('DashboardLayout');
  }, []);
  
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const { isInitialized } = useConnectionStore();
  const { refreshDashboard } = useDashboardStore();

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Auto-collapse sidebar on tablet
      if (window.innerWidth < 1024 && window.innerWidth >= 768) {
        setSidebarCollapsed(true);
      }
      
      // Close mobile menu when switching to desktop
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Only refresh dashboard data if connection is already initialized (prevents duplicate calls)
  useEffect(() => {
    const refreshIfInitialized = async () => {
      if (isInitialized) {
        try {
          // Only refresh dashboard data, connection check handled by main Dashboard component
          await refreshDashboard();
        } catch (error) {
          console.error('Failed to refresh dashboard data:', error);
        }
      }
    };

    refreshIfInitialized();
  }, [isInitialized, refreshDashboard]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="h-screen flex bg-background">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
          currentPath={location.pathname}
        />
      )}

      {/* Mobile Navigation Overlay */}
      {isMobile && (
        <MobileNav
          isOpen={mobileMenuOpen}
          onClose={closeMobileMenu}
          currentPath={location.pathname}
        />
      )}

      {/* Main Content Area */}
      <div className={cn(
        "flex-1 flex flex-col min-w-0",
        // Add transition for sidebar toggle
        "transition-all duration-300 ease-in-out"
      )}>
        {/* Page Content */}
        <main className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto px-4 py-6 max-w-7xl h-full">
              {children || <Outlet />}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobile && mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default DashboardLayout;